// Mappers for Managed Attendance (Marcas Gestionadas)
import type {
  LC_GetManagedAttendanceMarksListItemOut,
  LC_GetManagedAttendanceMarksOut,
  ManagedAttendanceAction,
  ManagedAttendanceStatus,
  ManagedAttendanceRecord,
  ManagedAttendancePage,
} from '../types';
import { formatToDdMmYyyy, extractTimeInHhMmSs } from '@/utils';

// =====================================================
// Pattern-based Mapper Configuration (OCP-compliant)
// =====================================================

/**
 * Configuration for pattern-based string mapping
 * Allows extending patterns without modifying mapper code
 */
export type PatternMapperConfig<T extends string> = {
  patterns: Record<T, string[]>;
  defaultValue: T;
};

/**
 * Generic pattern-based mapper factory
 * Enables extensibility by injecting pattern configuration
 */
export function createPatternMapper<T extends string>(config: PatternMapperConfig<T>) {
  return (input: string): T => {
    const lower = (input || '').toLowerCase();
    for (const [type, matchers] of Object.entries(config.patterns)) {
      if (Array.isArray(matchers)) {
        if (matchers.some((matcher) => lower.includes(matcher) || lower === matcher)) {
          return type as T;
        }
      }
    }
    return config.defaultValue;
  };
}

// Action mapping configuration - can be extended without modifying mapper
const ACTION_MAPPER_CONFIG: PatternMapperConfig<ManagedAttendanceAction> = {
  patterns: {
    created: ['cread', 'created'],
    disabled: ['deshabilit', 'inhabilitad', 'disabled'],
    modified: ['modific', 'modified'],
    incident: ['inciden', 'incident'],
  },
  defaultValue: 'created',
};

// Status mapping configuration - can be extended without modifying mapper
const STATUS_MAPPER_CONFIG: PatternMapperConfig<ManagedAttendanceStatus> = {
  patterns: {
    pending: ['pendient', 'pending'],
    approved: ['aprob', 'approved'],
    rejected: ['rechaz', 'rejected'],
  },
  defaultValue: 'pending',
};

// Create reusable mappers from configurations
const mapAction = createPatternMapper(ACTION_MAPPER_CONFIG);
const mapStatus = createPatternMapper(STATUS_MAPPER_CONFIG);

/**
 * Builds a composite key for a managed attendance transport item.
 * Used as a stable fallback identifier when `markId` is absent.
 * Format: `{employeeRut}-{workdayDate}-{markTime}-{actionDate}-{createdDate}`
 */
export function buildManagedAttendanceCompositeKey(
  transport: LC_GetManagedAttendanceMarksListItemOut,
): string {
  return `${transport.employeeRut}-${transport.workdayDate}-${transport.markTime}-${transport.actionDate}-${transport.createdDate}`;
}

export function mapManagedAttendanceMarkOutToModel(
  transport: LC_GetManagedAttendanceMarksListItemOut,
): ManagedAttendanceRecord {
  // Use markId as the primary stable identifier now that the backend provides it.
  // Fall back to a composite key only if markId is absent.
  const id = transport.markId ?? buildManagedAttendanceCompositeKey(transport);

  return {
    id,
    markId: transport.markId ?? null,
    creationDate: formatToDdMmYyyy(transport.createdDate),
    action: mapAction(transport.actionName ?? ''),
    workdayDate: formatToDdMmYyyy(transport.workdayDate),
    markingTime: extractTimeInHhMmSs(transport.markTime) ?? '',
    eventName: transport.eventName ?? '',  // ✅ Keep original event name from backend
    originalMarkTime: extractTimeInHhMmSs(transport.originalMarkTime),
    modificationDate: formatToDdMmYyyy(transport.actionDate),
    status: mapStatus(transport.status ?? ''),
    // Using RUT as name since API doesn't provide name
    fullName: transport.employeeRut ?? '',
    area: transport.contractCompany ?? '', // Using company as area
    location: '', // Not provided
    errorType: '', // Not provided
    comment: null,
    rejectionComment: transport.rejectionComment ?? null,
    creatorUser: transport.modifiedBy ?? '',
    actionDate: formatToDdMmYyyy(transport.actionDate),
  };
}

export function mapManagedAttendancePage(
  transport: LC_GetManagedAttendanceMarksOut,
): ManagedAttendancePage {
  const records = (transport.data ?? []).map(
    mapManagedAttendanceMarkOutToModel,
  );
  const pagination = transport.pagination;
  return {
    data: records,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    totalRecords: pagination.totalRecords,
    totalPages: pagination.totalPages,
  };
}

// =====================================================
// Factory for Custom Mappers (OCP Extension Point)
// =====================================================

/**
 * Factory to create custom managed attendance mappers with different pattern configs
 * Usage: const customMapper = createManagedAttendanceMapperFactory(customConfig);
 * This allows extending behavior without modifying the base mapper
 */
export function createManagedAttendanceMapperFactory(config?: {
  actionConfig?: PatternMapperConfig<ManagedAttendanceAction>;
  statusConfig?: PatternMapperConfig<ManagedAttendanceStatus>;
}) {
  const actionMapperConfig = config?.actionConfig ?? ACTION_MAPPER_CONFIG;
  const statusMapperConfig = config?.statusConfig ?? STATUS_MAPPER_CONFIG;

  const customMapAction = createPatternMapper(actionMapperConfig);
  const customMapStatus = createPatternMapper(statusMapperConfig);

  return {
    mapManagedAttendanceMarkOutToModel: (
      transport: LC_GetManagedAttendanceMarksListItemOut,
    ): ManagedAttendanceRecord => ({
      id: transport.markId ?? buildManagedAttendanceCompositeKey(transport),
      markId: transport.markId,
      creationDate: formatToDdMmYyyy(transport.createdDate),
      action: customMapAction(transport.actionName ?? ''),
      workdayDate: formatToDdMmYyyy(transport.workdayDate),
      markingTime: extractTimeInHhMmSs(transport.markTime) ?? '',
      eventName: transport.eventName ?? '',
      originalMarkTime: extractTimeInHhMmSs(transport.originalMarkTime),
      modificationDate: formatToDdMmYyyy(transport.actionDate),
      status: customMapStatus(transport.status ?? ''),
      fullName: transport.employeeRut ?? '',
      area: transport.contractCompany ?? '',
      location: '',
      errorType: '',
      comment: null,
      rejectionComment: transport.rejectionComment ?? null,
      creatorUser: transport.modifiedBy ?? '',
      actionDate: formatToDdMmYyyy(transport.actionDate),
    }),
    mapManagedAttendancePage: (
      transport: LC_GetManagedAttendanceMarksOut,
    ): ManagedAttendancePage => {
      const mapRecord = (
        item: LC_GetManagedAttendanceMarksListItemOut,
      ): ManagedAttendanceRecord => ({
        id: item.markId ?? buildManagedAttendanceCompositeKey(item),
        markId: item.markId,
        creationDate: formatToDdMmYyyy(item.createdDate),
        action: customMapAction(item.actionName ?? ''),
        workdayDate: formatToDdMmYyyy(item.workdayDate),
        markingTime: extractTimeInHhMmSs(item.markTime) ?? '',
        eventName: item.eventName ?? '',
        originalMarkTime: extractTimeInHhMmSs(item.originalMarkTime),
        modificationDate: formatToDdMmYyyy(item.actionDate),
        status: customMapStatus(item.status ?? ''),
        fullName: item.employeeRut ?? '',
        area: item.contractCompany ?? '',
        location: '',
        errorType: '',
        comment: null,
        rejectionComment: item.rejectionComment ?? null,
        creatorUser: item.modifiedBy ?? '',
        actionDate: formatToDdMmYyyy(item.actionDate),
      });
      const records = (transport.data ?? []).map(mapRecord);
      const pagination = transport.pagination;
      return {
        data: records,
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        totalRecords: pagination.totalRecords,
        totalPages: pagination.totalPages,
      };
    },
  };
}
